import { formatNumber } from '@angular/common';
import { Injectable } from '@angular/core';
import { Subject } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class RangeService {
  private _initControl = new Subject();

  percent(min: number, max: number, value?: number): number {
    if (min > max) {
      return min;
    }
    const amount = max - min;
    value = this.valueOrDefault(min, max, value) - min;
    return (value * 100) / amount;
  }

  valueOrDefault(min: number, max: number, value?: number): number {
    if (typeof value === 'number') {
      return value;
    }
    return min > max ? min : min + (max - min) / 2;
  }

  init(
    min: number,
    max: number,
    currency: boolean,
    symbol: string,
    step?: number
  ) {
    if (currency && symbol === '%') {
      symbol = '$';
    }

    // calculate step if it was not passed
    if (!step) {
      step = (max - min) / 100;
    }

    return { symbol, step };
  }

  initForm<T>(value?: number, onChange?: (arg: T) => void) {
    if (!onChange) {
      this._initControl.next();
      return this._initControl.asObservable();
    }
    // need to wait a bit to init FormControls
    setTimeout(() => {
      if (value === undefined) {
        this._initControl.next();
      }
    }, 0);
    return this._initControl.asObservable();
  }

  format<T extends any>(value: T, currency: boolean, symbol: string): string {
    if (currency) {
      return `${symbol}${formatNumber(value as number, 'en-US', '1.2-2')}`;
    }
    // percent | default formatting
    return `${value}${symbol}`;
  }

  findStepIndex<T>(value: T, steps: T[]): number {
    if (!steps.length) {
      return -1;
    }
    let index = steps!.indexOf(value);
    if (index < 0) {
      // happens when passing a wrong object reference to component
      // ex.: passing a equal object but recreated, causing not referece problem

      // let's try a deep equality object compare
      index = steps!.findIndex((s) => this._isDeepStrictEqual(value, s));

      // not found anyway
      if (index < 0) {
        throw new ReferenceError('Item is not part of steps array!');
      }
    }
    return index;
  }

  private _isDeepStrictEqual(obj1: any, obj2: any): boolean {
    // try to compare without sorting keys first for better performance
    const equal = JSON.stringify(obj1) === JSON.stringify(obj2);
    if (equal) {
      return true;
    }
    // not equal, try with sorted keys
    return (
      JSON.stringify(this._sortObjectKeys(obj1)) ===
      JSON.stringify(this._sortObjectKeys(obj2))
    );
  }

  private _sortObjectKeys(obj: any) {
    return Object.keys(obj)
      .sort()
      .reduce((result: any, key) => {
        result[key] = obj[key];
        return result;
      }, {});
  }
}
